<template>
  <CustomModal v-model:visible="isVisible" :title="modalTitle" width="600px" :bodyStyle="{ padding: 0, height: '600px', overflow: 'hidden' }">
    <div class="stripe-checkout-container">
      <div v-if="loading" class="flex justify-center items-center py-12">
        <CustomLoader />
      </div>
      <div v-show="!loading" id="checkout" class="checkout-content"></div>
    </div>
  </CustomModal>
</template>

<script setup>
import { ref, watch, onUnmounted, nextTick } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import CustomModal from '@/components/custom/CustomModal.vue';
import CustomLoader from '@/components/custom/CustomLoader.vue';
import { STRIPE_PUBLISHABLE_KEY, ENVIRONMENT } from '../../../env.config';

// Global flag to track if any checkout is active
let globalCheckoutActive = false;

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  clientSecret: {
    type: String,
    required: true,
  },
  modalTitle: {
    type: String,
    default: 'Complete Your Payment',
  },
});

const emit = defineEmits(['update:visible', 'success', 'error']);

const isVisible = ref(props.visible);
const loading = ref(false);
// Make these specific to this component instance
const stripe = ref(null);
const checkout = ref(null);
const isInitializing = ref(false);

const initializeCheckout = async () => {
  if (!props.clientSecret || !isVisible.value || isInitializing.value) return;

  // Check global flag
  if (globalCheckoutActive) {
    console.warn('Another Stripe checkout is already active. Waiting...');
    return;
  }

  isInitializing.value = true;
  loading.value = true;
  globalCheckoutActive = true;

  try {
    // Destroy any existing checkout instance first
    if (checkout.value) {
      try {
        checkout.value.destroy();
        checkout.value = null;
      } catch (destroyError) {
        console.error('Error destroying existing checkout:', destroyError);
      }
    }

    // Wait for next tick to ensure DOM is updated
    await nextTick();

    // Initialize Stripe
    if (!stripe.value) {
      stripe.value = await loadStripe(STRIPE_PUBLISHABLE_KEY);
    }

    // Small delay to ensure modal is fully rendered
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Check if the element exists
    const checkoutElement = document.getElementById('checkout');
    if (!checkoutElement) {
      throw new Error('Checkout element not found in DOM');
    }

    // Create embedded checkout
    checkout.value = await stripe.value.initEmbeddedCheckout({
      clientSecret: props.clientSecret,
    });

    // Mount checkout to the DOM
    await checkout.value.mount('#checkout');

    loading.value = false;
    isInitializing.value = false;
  } catch (error) {
    console.error('Error initializing checkout:', error);
    loading.value = false;
    isInitializing.value = false;
    globalCheckoutActive = false;
    emit('error', error);

    // Don't close modal on error, let user close it manually
    // emit('update:visible', false);
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    isVisible.value = newVal;
    if (newVal && props.clientSecret) {
      // Wait a bit for modal to be fully rendered
      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 50));
      initializeCheckout();
    } else if (!newVal && checkout.value) {
      // Cleanup when modal closes
      try {
        checkout.value.destroy();
        checkout.value = null;
        globalCheckoutActive = false;
        // Don't emit error here - let the isVisible watcher handle it
      } catch (error) {
        console.error('Error destroying checkout:', error);
      }
    }
  }
);

// Also watch for isVisible changes (when user clicks outside modal)
watch(isVisible, (newVal) => {
  if (!newVal && props.visible) {
    // Modal was closed by user (not programmatically)
    emit('update:visible', false);
    emit('error', { message: 'Checkout cancelled by user' });
  }
});

watch(
  () => props.clientSecret,
  async (newVal, oldVal) => {
    // Only initialize if clientSecret actually changed and modal is visible
    if (newVal && newVal !== oldVal && isVisible.value) {
      await nextTick();
      initializeCheckout();
    }
  }
);

onUnmounted(() => {
  if (checkout.value) {
    try {
      checkout.value.destroy();
      checkout.value = null;
    } catch (error) {
      console.error('Error destroying checkout on unmount:', error);
    }
  }
  // Also cleanup stripe instance
  if (stripe.value) {
    stripe.value = null;
  }
  // Reset global flag
  globalCheckoutActive = false;
});
</script>

<style scoped>
.stripe-checkout-container {
  height: 600px;
  width: 100%;
  overflow: hidden;
  position: relative;
  margin-bottom: 10px;
}

#checkout {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.checkout-content {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Custom scrollbar styling */
.checkout-content::-webkit-scrollbar {
  width: 6px;
}

.checkout-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.checkout-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.checkout-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
